import { z } from "zod";
import zodToJsonSchema from "zod-to-json-schema";

/*
 * Copied from the form-service backend
 * (src/resources/admin/rules/configuration/interface.ts) so the import editor
 * can validate a pasted rule configuration client-side with the exact same
 * rules the server enforces, including the cross-rule superRefine invariants.
 * Keep this in sync with the backend schema.
 */

export const RuleType = z.enum([
  "ConnectForms",
  "AutoRevoke",
  "ClearLowerRanked",
  "AutoDecision",
  "AutoPlacement",
  "DynamicSibling",
  "FormQuestion",
]);

const RuleSchoolsSchema = z.union([z.literal("any"), z.array(z.string())]);

const LegacyRuleSchema = z.object({
  rule_type: z.literal(RuleType.Enum.ConnectForms),
});

export const ConnectFormsStatus = z.enum([
  "FormAdmissions",
  "FormCanceled",
  "FormDeleted",
  "FormInProgress",
  "FormLotteryReady",
  "FormSubmitted",
  "FormVerified",
  "FormWithdrawn",
  "OfferAccepted",
  "OfferDeclined",
  "OfferOffered",
  "OfferRevoked",
  "WaitlistRemoved",
  "WaitlistWaitlisted",
  "WaitlistWithdrawn",
]);

const LegacyRuleType = z.enum(["StatusOrSubstatus"]);

const LegacyRuleStatusOrSubstatusSchema = LegacyRuleSchema.extend({
  condition: z.literal(LegacyRuleType.Enum.StatusOrSubstatus),
});

const LegacyRuleStatusOrSubstatusAction = z.enum(["ConnectToForm"]);

const LegacyRuleConnectFormSchema = LegacyRuleStatusOrSubstatusSchema.extend({
  action: z.literal(LegacyRuleStatusOrSubstatusAction.Enum.ConnectToForm),
  status: ConnectFormsStatus,
  school_reference_ids: RuleSchoolsSchema,
  connected_form_template_key: z.string().min(1, {
    message: "The form_template_key for this legacy rule is not set.",
  }),
  title: z.string(),
  subtitle: z.string(),
  text_to_display_when_continuing: z.string(),
  main_button_text: z.string(),
});

const RuleBaseSchema = z.object({
  name: z.string(),
  description: z.string(),
  key: z.string().min(1, {
    message: "The key for this rule configuration is not set.",
  }),
  active: z.boolean(),
});

const AutoRevokeRuleSchema = RuleBaseSchema.extend({
  rule_type: z.literal(RuleType.Enum.AutoRevoke),
  school_reference_ids: RuleSchoolsSchema,
  expiration_days: z.number().int().min(0),
  expiration_day_type: z.enum(["Calendar", "Weekday"]),
  expiration_time: z.string().regex(/^([0-1][0-9]|2[0-3]):(00|30):00$/),
});

const ClearLowerRankedRuleSchema = RuleBaseSchema.extend({
  rule_type: z.literal(RuleType.Enum.ClearLowerRanked),
  remove_lower_ranked_waitlists: z.boolean(),
  revoke_lower_ranked_offers: z.boolean(),
});

const AutoRuleFormStatus = z.enum([
  "Admissions",
  "Submitted",
  "Verified",
  "ReadyForLottery",
]);

const AutoPlacementRuleSchema = RuleBaseSchema.extend({
  rule_type: z.literal(RuleType.Enum.AutoPlacement),
  school_reference_ids: RuleSchoolsSchema,
  tag_names: z.array(z.string()).default([]),
  status: z.array(AutoRuleFormStatus).min(1),
}).superRefine((rule, ctx) => {
  if (!rule.status.includes("Submitted")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "The Submitted status must be included in the rule.",
      path: ["status"],
    });
  }
});

const AutoDecisionRuleSchema = RuleBaseSchema.extend({
  rule_type: z.literal(RuleType.Enum.AutoDecision),
  school_reference_ids: RuleSchoolsSchema,
  status: z.array(AutoRuleFormStatus).min(1),
  can_make_offers: z.boolean(),
  offer_from_waitlist_only: z.boolean().optional().default(false),
}).superRefine((rule, ctx) => {
  if (rule.offer_from_waitlist_only && !rule.can_make_offers) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "Offers from the waitlist only requires automatic offers to be enabled.",
      path: ["offer_from_waitlist_only"],
    });
  }
});

const TagRuleBaseSchema = RuleBaseSchema.extend({
  tag_names: z
    .array(z.string())
    .min(1, { message: "The tag name is not set." }),
  remove_when_condition_is_not_met: z.boolean(),
});

const DynamicSiblingStatuses = z.enum([
  "OfferAccepted",
  "OfferDeclined",
  "OfferOffered",
  "OfferRevoked",
  "WaitlistRemoved",
  "WaitlistWaitlisted",
  "WaitlistWithdrawn",
]);

const DynamicSiblingRuleSchema = TagRuleBaseSchema.extend({
  rule_type: z.literal(RuleType.Enum.DynamicSibling),
  school_reference_ids: RuleSchoolsSchema,
  allowed_substatuses: z.union([
    z.literal("any"),
    z.array(DynamicSiblingStatuses),
  ]),
});

const FormQuestionType = z.enum([
  "Address",
  "Grades",
  "SingleSelect",
  "MultiSelect",
]);

const FormQuestionRuleSchema = TagRuleBaseSchema.extend({
  rule_type: z.literal(RuleType.Enum.FormQuestion),
  school_reference_ids: RuleSchoolsSchema,
  question_key: z.string().min(1, {
    message: "The question key for this form question rule is not set.",
  }),
});

const GradesFormQuestionRuleSchema = FormQuestionRuleSchema.extend({
  form_question_type: z.literal(FormQuestionType.Enum.Grades),
  grade_config_keys: z.union([z.literal("any"), z.array(z.string())]),
});

const SingleSelectFormQuestionRuleSchema = FormQuestionRuleSchema.extend({
  form_question_type: z.literal(FormQuestionType.Enum.SingleSelect),
  answer_option_keys: z.union([z.literal("any"), z.array(z.string())]),
});

const MultiSelectFormQuestionRuleSchema = FormQuestionRuleSchema.extend({
  form_question_type: z.literal(FormQuestionType.Enum.MultiSelect),
  answer_option_keys: z.union([z.literal("any"), z.array(z.string())]),
});

const AddressFormQuestionRuleSchema = FormQuestionRuleSchema.extend({
  form_question_type: z.literal(FormQuestionType.Enum.Address),
});

const AddressFormQuestionBoundarySelectionType = z.enum([
  "Selected",
  "Specific",
]);

const AddressFormQuestionSelectedBoundaryRuleSchema =
  AddressFormQuestionRuleSchema.extend({
    boundary_selection_type: z.literal(
      AddressFormQuestionBoundarySelectionType.Enum.Selected
    ),
    is_inside: z.boolean(),
  });

const AddressFormQuestionSpecificBoundaryRuleSchema =
  AddressFormQuestionRuleSchema.extend({
    boundary_selection_type: z.literal(
      AddressFormQuestionBoundarySelectionType.Enum.Specific
    ),
    is_inside: z.boolean(),
    boundary_keys: z.array(
      z.string().min(1, {
        message: "The boundary key for this address question rule is not set.",
      })
    ),
    school_reference_ids: RuleSchoolsSchema,
  });

export const RuleSchema = z.union([
  LegacyRuleConnectFormSchema,
  AutoRevokeRuleSchema,
  ClearLowerRankedRuleSchema,
  AutoDecisionRuleSchema,
  AutoPlacementRuleSchema,
  DynamicSiblingRuleSchema,
  GradesFormQuestionRuleSchema,
  SingleSelectFormQuestionRuleSchema,
  MultiSelectFormQuestionRuleSchema,
  AddressFormQuestionSelectedBoundaryRuleSchema,
  AddressFormQuestionSpecificBoundaryRuleSchema,
]);
export type Rule = z.infer<typeof RuleSchema>;

export const RuleImportExportSchema = z
  .array(RuleSchema)
  .superRefine((rules, ctx) => {
    const seenKeys = new Map<string, number>();
    let connectFormsCount = 0;
    let clearLowerRankedCount = 0;

    rules.forEach((rule, index) => {
      // 1. at most one ConnectForms (the only leaf with no `key`)
      if (rule.rule_type === "ConnectForms") {
        connectFormsCount += 1;
        if (connectFormsCount > 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Only one ConnectForms rule is allowed.",
            path: [index, "rule_type"],
          });
        }
        return; // no `key` to check
      }

      if (rule.rule_type === "ClearLowerRanked") {
        clearLowerRankedCount += 1;
        if (clearLowerRankedCount > 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Only one ClearLowerRanked rule is allowed.",
            path: [index, "rule_type"],
          });
        }
      }

      // 2. every other rule extends RuleBaseSchema -> unique `key`
      const priorIndex = seenKeys.get(rule.key);
      if (priorIndex !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate key "${rule.key}" (already used at index ${priorIndex}).`,
          path: [index, "key"],
        });
      } else {
        seenKeys.set(rule.key, index);
      }
    });
  });

export type RuleImportExport = z.infer<typeof RuleImportExportSchema>;

/** JSON Schema used to drive live validation inside the JSON editor. */
export const RuleImportExportJsonSchema = zodToJsonSchema(
  RuleImportExportSchema
);
